<!--@format-->
<template>
    <div class="add-existing-solution-widget">
        <div class="widget-contents-container">
            <div class="widget-header">
                <h3>Add Solutions</h3>
                <div class="svg-container widget-close-button" @click="closeWidget">
                    <widget-close-svg />
                </div>
            </div>
            <div class="content-wrapper">
                <div class="bordered-container">
                    <div class="title">Solutions added to bundle</div>
                    <ul class="selected-solutions-list">
                        <li
                            v-for="(item, index) in selectedSolutions"
                            :key="item.id"
                            class="selected-solutions-item"
                        >
                            <img
                                :src="item.productImage | defaultImagePlaceholder"
                                @error="replaceByPlaceholder"
                            />
                            <span class="name">{{ item.name }}</span>
                            <span class="price">{{ item.price | currencyTwoDecimals }}</span>
                            <app-button
                                class="transparent-red-btn"
                                @click="handleRemoveSelected(item, index)"
                                >Remove <trash-can-svg
                            /></app-button>
                        </li>
                    </ul>
                </div>
                <div class="search-tools">
                    <div class="searchbar-container">
                        <app-input
                            :value="queryOptions.search"
                            placeholder="Search"
                            @input="handleSearch"
                        >
                            <div slot="inputIcon" class="input-icon">
                                <magnifying-glass-svg />
                            </div>
                        </app-input>
                    </div>
                    <div class="filter-buttons-container">
                        <solutions-sort-button
                            :is-open="isSortOpen"
                            :sort-options="sortOptions"
                            :applied-sort="queryOptions.sort"
                            @update:open="toggleOpenPopup()"
                            @select:sort="handleSort"
                        />
                    </div>
                </div>
                <div class="existing-solutions-table">
                    <loading-screen-svg v-if="isLoadingSearch" />
                    <template v-else>
                        <div class="solutions-header">
                            <span class="theader"> Name </span>
                            <span class="theader"> Unit Price </span>
                        </div>
                        <div ref="solutionsList" class="solutions-body" @scroll="handleScroll">
                            <div
                                v-for="item in availableSolutions"
                                :key="item.id"
                                class="solutions-row"
                            >
                                <div class="solutions-details-container">
                                    <app-button @click="handleAddSelected(item)">
                                        <o6-add-svg />
                                    </app-button>
                                    <img
                                        :src="item.productImage | defaultImagePlaceholder"
                                        @error="replaceByPlaceholder"
                                    />
                                    <span>{{ item.name }}</span>
                                </div>
                                <div class="solutions-price-container">
                                    {{ item.price | currencyTwoDecimals }}
                                </div>
                            </div>
                        </div>
                    </template>
                </div>
            </div>
            <div class="widget-footer">
                <div class="cancel-container">
                    <app-button class="cancel-btn transparent-btn" @click="closeWidget">
                        Go back
                    </app-button>
                </div>
                <div class="action-container">
                    <app-button @click="save">Update Solutions</app-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
const O6AddSvg = () => import('@/components/svg/grid/o6-add-svg');
const AppButton = () => import('@/components/common/app-button');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg.vue');
const LoadingScreenSvg = () => import('@/components/svg/loading-screen-svg');
const MagnifyingGlassSvg = () => import('@/components/svg/magnifying-glass-svg');
const SolutionsSortButton = () =>
    import('@/components/sliding-widgets/document/solution-widget/solutions-sort-button');
const AppInput = () => import('@/components/common/app-input');
const TrashCanSvg = () => import('@/components/svg/trash-can-svg');

import { SolutionBoardServiceMixin } from '@/mixins/board/solution-board-service-mixin';
import EventBus from '@/scripts/event-bus.js';

import _ from 'lodash';
import { debounce } from 'lodash';

export default {
    name: 'add-existing-solution-widget',

    components: {
        'o6-add-svg': O6AddSvg,
        'app-button': AppButton,
        'widget-close-svg': WidgetCloseSvg,
        'loading-screen-svg': LoadingScreenSvg,
        'trash-can-svg': TrashCanSvg,
        'app-input': AppInput,
        'solutions-sort-button': SolutionsSortButton,
        'magnifying-glass-svg': MagnifyingGlassSvg,
    },

    mixins: [SolutionBoardServiceMixin],

    props: {
        widget: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            isSortOpen: false,
            isLoadingSearch: false,
            solutions: [],
            availableSolutions: [],
            selectedSolutions: [],
            solutionImagePlaceholder: 'https://cdn.honeycrm.com/app/image-placeholder.svg',

            queryOptions: {
                search: null,
                sort: {
                    id: 1,
                    label: 'Alphabetical ascending',
                    property: 'Name',
                    direction: 'asc',
                },

                page: 1,
                itemsPerPage: 10,
            },

            sortOptions: [
                {
                    id: 0,
                    label: 'Alphabetical descending',
                    property: 'Name',
                    direction: 'desc',
                },
                {
                    id: 1,
                    label: 'Alphabetical ascending',
                    property: 'Name',
                    direction: 'asc',
                },
                {
                    id: 2,
                    label: 'Price high to low',
                    property: 'UnitPrice',
                    direction: 'desc',
                },
                {
                    id: 3,
                    label: 'Price low to high',
                    property: 'UnitPrice',
                    direction: 'asc',
                },
            ],
        };
    },

    created() {
        this.selectedSolutions = [...this.widget.currentBundleItems];
        this.getCustomSolutions(this.queryOptions, true);
    },

    methods: {
        replaceByPlaceholder(event) {
            event.target.src = this.solutionImagePlaceholder;
        },

        handleScroll() {
            const container = this.$refs.solutionsList;
            const scrollPosition = container.scrollTop + container.clientHeight;
            const totalHeight = container.scrollHeight;

            if (scrollPosition >= totalHeight && !this.isLoadingSearch) {
                this.queryOptions.page++;
                this.getCustomSolutions(this.queryOptions);
            }
        },

        handleAddSelected(item) {
            this.selectedSolutions.push(item);
            this.getAvailableCustomSolutions();
        },

        handleRemoveSelected(item, index) {
            this.selectedSolutions.splice(index, 1);
            this.getAvailableCustomSolutions();
        },

        handleSearch: debounce(function(value) {
            this.queryOptions.search = value;
            this.resetResults();
            this.getCustomSolutions(this.queryOptions, true);
        }, 1000),

        closePopup() {
            this.isSortOpen = false;
        },

        toggleOpenPopup() {
            this.isSortOpen = true;
        },

        resetResults() {
            this.queryOptions.page = 1;
            this.solutions = [];
        },

        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        handleSort(value) {
            this.closePopup();
            if (this.sortOptions.sort && this.sortOptions.sort.id === value.id) {
                this.queryOptions.sort = null;
            } else {
                this.queryOptions.sort = value;
            }

            this.resetResults();
            this.getCustomSolutions(this.queryOptions, true);
        },

        save() {
            EventBus.$emit('bundles:add-existing-solutions', this.selectedSolutions);
            this.closeWidget();
        },

        asBundleItems(items) {
            return items.map((item) => ({
                id: item.id,
                name: item.name,
                price: item.unitPrice,
                description: item.description,
                productImage: item.previewImageUrl,
                mimeType: item.fileMimeType || this.getFileMimeTypeFromUrl(item.previewImageUrl),
                productImageName: null,
                qty: 1,
                subtotal: item.unitPrice,
                action: 'create',
            }));
        },

        getFileMimeTypeFromUrl(url) {
            if (url) {
                const parsedURL = new URL(url);
                const pathname = parsedURL.pathname;
                const lastDotIndex = pathname.lastIndexOf('.');
                if (lastDotIndex !== -1) {
                    const fileExtension = pathname.slice(lastDotIndex);
                    const fileType = fileExtension.slice(1);
                    return `image/${fileType}`;
                } else {
                    return null;
                }
            }
            return null;
        },

        getAvailableCustomSolutions() {
            this.availableSolutions = _.differenceBy(this.solutions, this.selectedSolutions, 'id');
        },

        getCustomSolutions(options, showLoading) {
            this.isLoadingSearch = showLoading;
            this.dispatchGetSolutions({ options })
                .then(({ items }) => {
                    let filteredItems = this.widget.currentSolutionId
                        ? items.filter((item) => item.id !== this.widget.currentSolutionId)
                        : items;
                    this.solutions = [...this.solutions, ...this.asBundleItems(filteredItems)];
                    this.getAvailableCustomSolutions();
                })
                .catch(() => {
                    this.$helpers.showFailNotification(
                        'An error occurred while fetching your solutions.',
                    );
                })
                .finally(() => {
                    this.isLoadingSearch = false;
                });
        },
    },
};
</script>

<style>
.add-existing-solution-widget .bordered-container {
    border: 2px solid #e9e9ef;
    border-radius: 22px;
    margin-bottom: 2.5em;
    padding: 32px;
    position: relative;
}

.add-existing-solution-widget .bordered-container .title {
    background-color: #ffffff;
    background-clip: content-box;
    font: 500 16px/24px 'Roboto';
    padding: 0px 30%;
    text-align: center;
    position: absolute;
    top: -11px;
    left: 0;
    width: 100%;
}

.add-existing-solution-widget .bordered-container hr {
    border-top: 2px solid #e9e9ef;
    margin: 0;
}

.add-existing-solution-widget .search-tools {
    display: flex;
    gap: 12px;
    height: 72px;
    justify-content: space-between;
    padding: 16px 24px;
}

.add-existing-solution-widget .search-tools .searchbar-container {
    flex: 1;
}

.add-existing-solution-widget .search-tools .searchbar-container .app-input .icon-container {
    top: 8px;
    right: 12px;
}

.add-existing-solution-widget .search-tools .filter-buttons-container {
    display: flex;
    gap: 8px;
}

.add-existing-solution-widget .app-input .input-icon .magnifying-glass-svg path {
    fill: #f30070;
}

.add-existing-solution-widget .existing-solutions-table {
    width: 100%;
}

.add-existing-solution-widget .existing-solutions-table .solutions-header {
    display: flex;
    justify-content: space-around;
    width: 100%;
    margin-bottom: 16px;
    font: normal normal 500 10px/21px 'Roboto';
    letter-spacing: 0px;
    color: #382153;
    text-transform: uppercase;
}

.add-existing-solution-widget .existing-solutions-table .solutions-body {
    border: 2px solid #e9e9ef;
    border-radius: 16px;
    max-height: 450px;
    overflow-y: auto;
}

.add-existing-solution-widget .existing-solutions-table .solutions-row {
    display: flex;
    width: 100%;
    align-items: center;
}

.add-existing-solution-widget .existing-solutions-table .solutions-row:not(:last-child) {
    border-bottom: 2px solid #e9e9ef;
}

.add-existing-solution-widget
    .existing-solutions-table
    .solutions-row
    .solutions-details-container {
    display: flex;
    width: 100%;
    gap: 24px;
    border-right: 1px solid #e9e9ef;
    align-items: center;
    padding: 8px;
}

.add-existing-solution-widget
    .existing-solutions-table
    .solutions-row
    .solutions-details-container
    .app-button {
    width: 35px;
    height: 35px;
    background: #18aeff 0% 0% no-repeat padding-box;
    box-shadow: 0px 3px 12px #187cff44;
    border-radius: 31px;
    padding: 0;
}

.add-existing-solution-widget
    .existing-solutions-table
    .solutions-row
    .solutions-details-container
    .app-button
    .o6-add-svg
    path {
    fill: #fff;
}

.add-existing-solution-widget
    .existing-solutions-table
    .solutions-row
    .solutions-details-container
    img {
    width: 36px;
    height: 36px;
    border-radius: 6px;
}

.add-existing-solution-widget
    .existing-solutions-table
    .solutions-row
    .solutions-details-container
    span {
    text-align: left;
    font: normal normal normal 14px/21px 'Roboto';
    letter-spacing: 0px;
    color: #382153;
    max-width: 115px;
}

.add-existing-solution-widget .existing-solutions-table .solutions-row .solutions-price-container {
    display: flex;
    width: 100%;
    /* margin: 0 auto; */
    justify-content: center;
}

.add-existing-solution-widget .selected-solutions-list .selected-solutions-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
    margin-top: 8px;
}

.add-existing-solution-widget .action-container .app-button.btn.normal {
    width: 100%;
}

.add-existing-solution-widget .cancel-container .cancel-btn.transparent-btn.app-button.btn.normal {
    background: transparent;
}

.add-existing-solution-widget
    .selected-solutions-list
    .selected-solutions-item
    .app-button.transparent-red-btn {
    background: #f30070;
    width: 120px;
    min-width: 120px;
}

.add-existing-solution-widget
    .selected-solutions-list
    .selected-solutions-item
    .app-button.transparent-red-btn
    .trash-can-svg {
    width: 11px;
    height: 14px;
    margin-left: 8px;
}

.add-existing-solution-widget
    .selected-solutions-list
    .selected-solutions-item
    .app-button.transparent-red-btn
    .trash-can-svg
    path,
.add-existing-solution-widget
    .selected-solutions-list
    .selected-solutions-item
    .app-button.transparent-red-btn
    .trash-can-svg
    rect {
    fill: #fff;
}

.add-existing-solution-widget .selected-solutions-list .selected-solutions-item span.name {
    padding-right: 16px;
    border-right: 1px solid #cac6cb;
    margin-right: 16px;
    width: 130px;
}

.add-existing-solution-widget .selected-solutions-list .selected-solutions-item span {
    font: normal normal normal 14px/25px 'Roboto';
    letter-spacing: 0px;
    color: #382153;
}

.add-existing-solution-widget .selected-solutions-list .selected-solutions-item span.price {
    margin-right: auto;
}

.add-existing-solution-widget .selected-solutions-list .selected-solutions-item img {
    width: 36px;
    height: 36px;
    border-radius: 6px;
}

.add-existing-solution-widget .existing-solutions-table *::-webkit-scrollbar {
    border-radius: 16px;
    width: 12px;
}

.add-existing-solution-widget .existing-solutions-table *::-webkit-scrollbar-thumb {
    border-radius: 32px;
    background: #e9e9ef;
    border: 1px solid transparent;
    background-clip: content-box;
}

.add-existing-solution-widget .existing-solutions-table .loading-screen {
    margin: 170px;
}
</style>
